import EnvironmentManager from 'Utils/environmentManager';
import mixpanel from 'Utils/mixpanel';

// Global feature flags live in main CMS (messages), not tjkMessages.
// getMessageText switches to tjkMessages on /tjk routes, so read main messages directly.
const getMainCmsBoolean = key => {
  // getMainMessageText() always returns a string (never null/undefined), so `??` against it
  // would never fall through to getParameter() - use getMainMessage() to preserve
  // "fall back only when the key isn't found at all", not "when its value is falsy".
  const raw = EnvironmentManager.getMainMessage(key)?.value ?? EnvironmentManager.getParameter(key);

  if (raw === true) return true;
  if (raw === false) return false;

  return String(raw ?? '').trim().toLowerCase() === 'true';
};

export const isDepositPageFastDepositEnabled = () =>
  getMainCmsBoolean('deposit_page_fast_deposit_switch_v2');

export const isLowBalanceFastDepositFeatureEnabled = () => {
  if (!isDepositPageFastDepositEnabled()) {
    return false;
  }

  return getMainCmsBoolean('low_balance_fast_deposit_switch');
};

export const isLowBalanceFastDepositEnabled = isLowBalanceFastDepositFeatureEnabled;

export const getLowBalanceFastDepositSwitchValue = () =>
  (getMainCmsBoolean('low_balance_fast_deposit_switch') ? 'True' : 'False');

const CLICK_POINT_BY_GAME_TYPE = {
  iddaa: 'Low Balance-Iddaa',
  sportoto: 'Low Balance-Spor Toto',
  horseracing: 'Low Balance-Horse Racing',
};

export const getLowBalanceClickPoint = gameType =>
  CLICK_POINT_BY_GAME_TYPE[gameType] || CLICK_POINT_BY_GAME_TYPE.iddaa;

export const FAST_DEPOSIT_PAGE_CLICK_POINT = 'Fast Deposit Page';

export const getHowToDepositPlayClickPoint = (origin, gameType) => {
  if (origin === 'fastDeposit') {
    return FAST_DEPOSIT_PAGE_CLICK_POINT;
  }

  return getLowBalanceClickPoint(gameType);
};

export const trackClickPlayButtonOnHowToDepositBottomsheet = clickPoint => {
  mixpanel.track('Click Play Button on How to Deposit Bottomsheet', {
    'Click Point': clickPoint,
  });
};

export const getLowBalanceDescription = cms =>
  cms('low_balance_fast_deposit_description') || 'Kuponu oynayabilmek için bakiyen yetersiz.';

export const getLowBalanceCardSubtitle = cms =>
  cms('low_balance_fast_deposit_new_subtitle')
  || cms('low_balance_fast_deposit_subtitle')
  || 'Bilyoner Anında ile Hızlı Para Yatır';

export const getLowBalanceScreenType = isFullScreen =>
  (isFullScreen ? 'Low Balance-Full Screen' : 'Low Balance-Actionsheet');

export const trackViewLowBalanceScreen = ({ isFullScreen }) => {
  mixpanel.track('View Low Balance Screen', {
    'Fast Deposit Switch': getLowBalanceFastDepositSwitchValue(),
    'Screen Type': getLowBalanceScreenType(isFullScreen),
  });
};

export const trackClickSaveCouponButton = gameType => {
  mixpanel.track('Click Save Coupon Button', {
    'Click Point': getLowBalanceClickPoint(gameType),
    'Fast Deposit Switch': getLowBalanceFastDepositSwitchValue(),
  });
};

export const trackClickDepositPageButton = gameType => {
  mixpanel.track('Click Deposit Page Button', {
    'Click Point': getLowBalanceClickPoint(gameType),
  });
};

export const trackClickDepositMoneyButton = gameType => {
  mixpanel.track('Click Deposit Money Button', {
    'Click Point': getLowBalanceClickPoint(gameType),
  });
};

export const trackClickSendToBilyonerFromLowBalance = gameType => {
  mixpanel.track('Click Send to Bilyoner Button on Fast Deposit Page', {
    'Click Point': getLowBalanceClickPoint(gameType),
  });
};

export const trackClickSendToBilyonerFromDepositPage = () => {
  mixpanel.track('Click Send to Bilyoner Button on Fast Deposit Page', {
    'Click Point': 'Deposit Page',
  });
};

export const trackClickSendToBilyonerFromPendingPage = () => {
  mixpanel.track('Click Send to Bilyoner Button on Fast Deposit Page', {
    'Click Point': 'Pending Page',
  });
};

export const shouldRedirectToLowBalanceFastDeposit = getState =>
  getState().userAgent.device.type === 'mobile' && isLowBalanceFastDepositEnabled();

export const getLowBalanceOnboardingVideoUrl = () => {
  if (!isLowBalanceFastDepositFeatureEnabled()) return null;

  const message = EnvironmentManager.messages?.find(item => item?.key === 'low_balance_fast_deposit_onboarding_video_url');
  const value = message?.value ?? EnvironmentManager.getParameter('low_balance_fast_deposit_onboarding_video_url');
  if (value == null) return null;

  const url = String(value).trim();
  if (!url || url.toLowerCase() === 'null' || url.toLowerCase() === 'undefined') {
    return null;
  }

  return url;
};

export const getLowBalanceOnboardingVideoEmbedUrl = () => {
  const url = getLowBalanceOnboardingVideoUrl();
  if (!url) return null;

  const match = url.match(/(?:v=|youtu\.be\/)([^&?/]+)/);
  return match ? `https://www.youtube.com/embed/${match[1]}` : null;
};

export const getLowBalanceFastDepositImage = () => {
  if (!isLowBalanceFastDepositFeatureEnabled()) return null;

  const message = EnvironmentManager.messages?.find(item => item?.key === 'low_balance_fast_deposit_image');
  const value = message?.value ?? EnvironmentManager.getParameter('low_balance_fast_deposit_image');
  if (value == null) return null;

  const url = String(value).trim();
  if (!url || url.toLowerCase() === 'null' || url.toLowerCase() === 'undefined') {
    return null;
  }

  return url;
};
